import React, { useEffect, useState } from 'react'
import { Header } from '@/components/Header'
import './index.less'
import { CloseOutlined } from '@ant-design/icons'
import { downBtn, moon, sun, upBtn } from '@/assets/images'
import * as d3 from 'd3'
import MiniMap from '@/components/MiniMap'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { getPaintingIntro, SRC_URL } from '@/server/'
import {
  getImagesOfWorkRequest,
  getImgsInfoByImgID,
  getSixLawsInfo,
  getWorkMetadata
} from '@/server'
import { TagBox } from './TagBox'
import { SelectWorkList } from '@/components/SelectWorkList'
import { message, Tooltip } from 'antd'
import {
  background,
  setDetailsPageBackground
} from '@/features/SetBackgroundColor/setBackgroundColor'
import { useNavigate, useParams } from 'react-router-dom'
import { setToken } from '@/features/Login/login'

// 画作详情页组件
export function DetailsPage() {
  const token = useAppSelector((state) => state.login.token)
  const params = useParams()
  // 需要展示的作品workid
  const showWorkId = params.workId
  // 需要展示的画作imageid
  const showImageId = params.imageId
  // 背景颜色
  const backgroundColor = useAppSelector((state) => state.backgroundColor.detailsPageBackground)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  // 选中的选项index
  const [selectedMenuIndex, setSelectedMenuIndex] = useState<number>(0)
  // 列表展现画数
  const listSize = 7
  //展示画作的图片信息列表
  const [showWorkList, setShowWorkList] = useState<
    {
      id: string
      desc: string
      imageId: string
      width: number
      height: number
      levelMax: number
      levelMin: number
      tilesPath: string
      src: string
    }[]
  >([])
  // 选中的图片列表index
  const [selectedWorkIndex, setSelectedWorkIndex] = useState<number>(0)
  // 展示画作的文字信息
  const [showWorkInfo, setShowWorkInfo] = useState<{
    // 作品名称
    workName: string
    // 尺寸 [宽，高]
    size: number[] | null
    //
    mediaType: string
    //
    materialType: string
    // 作者名
    authorName: string
    // 朝代
    dynasty: string
    // 描述
    desc: string
    // 藏馆
    museum: string
    // 海外评级
    overseasrating: string
    // 国内评级
    domesticrating: string
  } | null>(null)
  // 是否显示上拉按键（控制画作选择列表）
  const [isDisplayPullUp, setIsDisplayPullUp] = useState<boolean>(false)
  // 是否显示隐藏按键（控制画作选择列表）
  const [isDisplayDownBtn, setIsDisplayDownBtn] = useState<boolean>(false)
  // 菜单选项
  const menuList = [
    '基础信息',
    '气韵生动',
    '骨法用笔',
    '应物象形',
    '随类赋彩',
    '经营位置',
    '传移模写'
  ]
  // 气韵生动数组
  const [qiyunArr, setQiyunArr] = useState<string[]>([])
  // 骨法用笔数组
  const [gufaArr, setGufaArr] = useState<string[]>([])
  // 应物象形数组
  const [yingwuArr, setYingwuArr] = useState<string[]>([])
  // 随类赋彩数组
  const [fucaiArr, setFucaiArr] = useState<
    {
      // 颜色名
      name: string
      color: string
      // 占比
      percentage: string
    }[]
  >([])
  // 经营位置数组
  const [jingyingArr, setJingyingArr] = useState<string[]>([])
  // 图片后缀名
  const [imgPostfix, setImgPostFix] = useState<string>('.png')
  // 传移模写数组
  const [chuanyiArr, setChuanyiArr] = useState<
    {
      workid: string
      imageid: string
      workname: string
      dynasty: string
      authorname: string
      src: string
      // 相似度
      sim: number
    }[]
  >([])

  /**
   * 选择菜单的回调
   * @param index：选项的索引
   */
  const handleSelectedIndex = function (index: number) {
    setSelectedMenuIndex(index)
  }
  /**
   * 切换背景颜色
   */
  const handleChangeBackgroundColor = function () {
    dispatch(
      setDetailsPageBackground(
        backgroundColor === background.SUN ? background.MOON : background.SUN
      )
    )
  }
  /**
   * 展开画作选择的回调
   */
  const handlePullUp = function () {
    const div = d3.select('#painting-select-content')
    div.style('transform', 'translate(0px,0px)').style('transition-duration', '.5s')
    //隐藏上拉按钮，显示隐藏按钮
    setIsDisplayPullUp(false)
    setIsDisplayDownBtn(true)
  }
  /**
   * 收起画作选择的回调
   */
  const handlePackUp = function () {
    const div = d3.select('#painting-select-content')
    //自适应大小后，content的height
    const height = div._groups[0][0].clientHeight
    div.style('transform', 'translate(0px,' + height + 'px)').style('transition-duration', '.5s')
    //显示上拉按钮，隐藏隐藏按钮
    setIsDisplayPullUp(true)
    setIsDisplayDownBtn(false)
  }
  /**
   * 获取谢赫六法信息
   * @param imageId：画作的imageId
   */
  const getSixLawsInfoByImageId = function (imageId: string) {
    // 获取六法信息
    getSixLawsInfo(token, imageId)
      .then((res) => {
        if (res.data.code === '10056') {
          // token失效
          window.confirm('登录已过期，请重新登录！')
          dispatch(setToken(''))
          navigate('/')
        } else {
          // 中心构图：山水:png/人物:jpeg【特判】
          if (res.data.globalTags.构图.includes('中心构图')) {
            getPaintingIntro(token, { workId: showWorkId }).then((res) => {
              if (res.data.content.class1 === '人物') setImgPostFix('.jpeg')
              else setImgPostFix('.png')
            })
          } else {
            setImgPostFix('.png')
          }
          // 获取气韵生动、经营位置、随类赋彩信息
          setQiyunArr(res.data.globalTags.意境)
          setJingyingArr(res.data.globalTags.构图)
          setFucaiArr(res.data.colorBar)
          // 获取传移模写信息
          const topThreeUuid: { uuid: string; sim: number }[] = []
          const list = res.data.recommend
          // 只展示前三副相似度最高的画作
          for (let i = 0; i < list.length; i++) {
            if (topThreeUuid.length >= 3) break
            if (list[i].uuid === imageId) continue
            topThreeUuid.push({ uuid: list[i].uuid, sim: list[i].sim })
          }
          // 获取三副画作的信息
          const promiseArr: Promise<any>[] = []
          for (let i = 0; i < topThreeUuid.length; i++)
            promiseArr.push(getImgsInfoByImgID(token, { imageId: topThreeUuid[i].uuid }))
          Promise.all(promiseArr).then((values) => {
            const imgArr: {
              workid: string
              imageid: string
              workname: string
              dynasty: string
              authorname: string
              src: string
              sim: number
            }[] = []
            for (let i = 0; i < values.length; i++) {
              const work = values[i].data.content
              imgArr.push({
                workid: work.workid,
                imageid: topThreeUuid[i].uuid,
                workname: work.workname,
                dynasty: work.dynasty,
                authorname: work.authorname,
                src: work.encoding,
                sim: topThreeUuid[i].sim
              })
            }
            setChuanyiArr(imgArr)
          })
          // 获取骨法用笔、应物象形信息
          const gufa: string[] = [],
            yingwu: string[] = []
          res.data.localInfo.map((info) => {
            info.tags.map((tag: { typeName: string; tags: string[] }) => {
              if (tag.typeName === '技法') {
                tag.tags.map((x) => {
                  if (!gufa.includes(x)) gufa.push(x)
                })
              }
              if (tag.typeName === '物象') {
                tag.tags.map((x) => {
                  if (!yingwu.includes(x)) yingwu.push(x)
                })
              }
            })
          })
          setGufaArr(gufa)
          setYingwuArr(yingwu)
        }
      })
      .catch((error) => console.log(error))
  }
  /**
   * 点击复制颜色值的回调
   * @param colorName： 颜色名
   */
  const handleCopyColor = function (colorName: string) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(colorName)
    } else {
      const textarea = document.createElement('textarea')
      document.body.appendChild(textarea)
      // 隐藏此输入框
      textarea.style.position = 'fixed'
      textarea.style.top = '10px'
      // 赋值
      textarea.value = colorName
      // 选中
      textarea.select()
      // 复制
      document.execCommand('copy', true)
      // 移除输入框
      document.body.removeChild(textarea)
    }
    message.success('复制成功！' + colorName)
  }
  /**
   * 点击传移模写跳转新画作
   * @param workId：作品workid
   * @param imageId：画作imageid
   */
  const handleClickImgItem = function (workId: string, imageId: string) {
    window.open('/details/' + workId + '/' + imageId)
  }
  // 切换作品
  useEffect(() => {
    //获取图像信息
    getImagesOfWorkRequest({ workId: showWorkId }, token)
      .then((res) => {
        const list = res.data.content.map((work, index) => {
          // 直接跳转到对应的image
          if (work.imageid === showImageId) setSelectedWorkIndex(index)
          return {
            id: work.workid,
            desc: work.desc,
            imageId: work.imageid,
            width: work.width,
            height: work.height,
            levelMax: work.tileslevelmax - 1,
            levelMin: work.tileslevelmin,
            tilesPath: work.tilespath,
            src: work.fullpath
          }
        })
        //若该作品不止一张图，显示隐藏按钮
        if (list.length > 1) setIsDisplayDownBtn(true)
        setShowWorkList(list)
      })
      .catch((error) => console.log(error))
    // 获取文字信息
    getWorkMetadata({ workId: showWorkId }, token)
      .then((res) => {
        const work = res.data.content
        setShowWorkInfo({
          workName: work.workname,
          size: work.horizontal ? [work.horizontal, work.vertical] : null,
          mediaType: work.mediatype,
          materialType: work.materialtype,
          authorName: work.authorname,
          dynasty: work.dynasty,
          desc: work.description,
          museum: work.instname,
          overseasrating: work.overseasrating,
          domesticrating: work.domesticrating
        })
      })
      .catch((error) => console.log(error))
  }, [showWorkId, showImageId])
  // 切换同一作品的不同画作，重新请求谢赫六法的信息
  useEffect(() => {
    if (showWorkList.length <= 0) return
    getSixLawsInfoByImageId(showWorkList[selectedWorkIndex].imageId)
  }, [showWorkList, selectedWorkIndex])
  // 动态计算随类赋彩Bar的长度
  useEffect(() => {
    for (let i = 0; i < fucaiArr.length; i++) {
      const parentDivWidth = d3.select('#fucai-colors')._groups[0][0].clientWidth
      const childDiv = d3.select('#id' + fucaiArr[i].color.split('#')[1])
      const percentage = Number(fucaiArr[i].percentage.split('%')[0])
      childDiv.style('width', parentDivWidth * percentage + 'px')
    }
  }, [fucaiArr, selectedMenuIndex])

  return (
    <div className={'detail-body ' + (isDisplayDownBtn ? 'detail-body-list-open' : '')}>
      <div className='detail-header-container'>
        <Header />
      </div>
      {/* 基本信息框 */}
      <div
        className='basic-info'
        style={{
          display: menuList[selectedMenuIndex] === '基础信息' ? 'block' : 'none'
        }}
      >
        <div className='closed-btn' onClick={() => setSelectedMenuIndex(-1)}>
          <CloseOutlined style={{ margin: 4, fontSize: 16 }} />
        </div>
        <div className='painting-name'>{showWorkInfo?.workName}</div>
        <div className='painting-size'>
          {showWorkInfo?.mediaType}&nbsp;
          {showWorkInfo?.materialType}{' '}
          {showWorkInfo?.size ? `${showWorkInfo?.size[0]} x ${showWorkInfo?.size[1]}厘米` : ''}
        </div>
        <div className='painter-info'>
          {showWorkInfo?.dynasty}
          &nbsp;&nbsp;
          {showWorkInfo?.authorName}
        </div>
        <div className='painting-museum'>{showWorkInfo?.museum}</div>
        <div className='painting-museum'>{showWorkInfo?.overseasrating}</div>
        <div className='painting-museum'>{showWorkInfo?.domesticrating}</div>
        <div className='painting-desc'>{showWorkInfo?.desc}</div>
      </div>
      {/* 六法框 */}
      <div
        className='painting-detail-box'
        style={{
          display:
            selectedMenuIndex !== -1 && menuList[selectedMenuIndex] !== '基础信息' ? 'flex' : 'none'
        }}
      >
        {/* 框右上角菜单 */}
        <div className='painting-detail-menu'>
          <div className='painting-detail-menu-btn'>
            <div className='square-btn' onClick={handleChangeBackgroundColor}>
              <img src={backgroundColor === background.SUN ? sun : moon} alt='' />
            </div>
            {/*<div className='square-btn'>/!*<img src={star} alt='' />*!/</div>*/}
            <div className='square-btn' onClick={() => setSelectedMenuIndex(-1)}>
              <CloseOutlined style={{ margin: 4, fontSize: 16 }} />
            </div>
          </div>
        </div>
        <div
          className='painting-detail'
          style={{
            order: menuList[selectedMenuIndex] === '气韵生动' ? 1 : 2
          }}
        >
          <div className='painting-detail-title'>气韵生动</div>
          <div className='qiyun-desc'>
            {qiyunArr.length > 0 ? <TagBox list={qiyunArr} /> : '暂无数据'}
          </div>
        </div>
        <div
          className='painting-detail'
          style={{
            order: menuList[selectedMenuIndex] === '骨法用笔' ? 1 : 2
          }}
        >
          <div className='painting-detail-title'>骨法用笔</div>
          {gufaArr.length > 0 ? <TagBox list={gufaArr} /> : '暂无数据'}
        </div>
        <div
          className='painting-detail'
          style={{
            order: menuList[selectedMenuIndex] === '应物象形' ? 1 : 2
          }}
        >
          <div className='painting-detail-title'>应物象形</div>
          {yingwuArr.length > 0 ? <TagBox list={yingwuArr} /> : '暂无数据'}
        </div>
        <div
          className='painting-detail'
          style={{
            order: menuList[selectedMenuIndex] === '随类赋彩' ? 1 : 2
          }}
        >
          <div className='painting-detail-title'>随类赋彩</div>
          {fucaiArr.length > 0 ? (
            <div className='fucai-colors' id='fucai-colors'>
              {fucaiArr.map((item) => {
                return (
                  <Tooltip title={'点击复制：' + item.name} key={item.name}>
                    <div
                      className='color-bar'
                      key={item.color}
                      id={'id' + item.color.split('#')[1]}
                      onClick={() => handleCopyColor(item.color)}
                    >
                      <div
                        className='fucai-eachColor-percentage'
                        style={{
                          backgroundColor: item.color,
                          color: 'white'
                        }}
                      >
                        {item.percentage}
                      </div>
                    </div>
                  </Tooltip>
                )
              })}
            </div>
          ) : (
            '暂无数据'
          )}
        </div>
        <div
          className='painting-detail'
          style={{
            order: menuList[selectedMenuIndex] === '经营位置' ? 1 : 2
          }}
        >
          <div className='painting-detail-title'>经营位置</div>
          {jingyingArr.length > 0 ? (
            <TagBox list={jingyingArr} imgSrc={jingyingArr[0] + imgPostfix} />
          ) : (
            '暂无数据'
          )}
        </div>
        <div
          className='painting-detail'
          style={{
            order: menuList[selectedMenuIndex] === '传移模写' ? 1 : 2
          }}
        >
          <div className='painting-detail-title'>传移模写</div>
          {chuanyiArr.length > 0 ? (
            <div className='tag-list'>
              {chuanyiArr.map((item) => {
                return (
                  <div
                    className='tag-item'
                    onClick={() => handleClickImgItem(item.workid, item.imageid)}
                  >
                    <img src={item.src ? SRC_URL + item.src : ''} alt='相似图' />
                    <div className='tag-desc'>
                      <div className='tag-work-name' title={item.workname}>
                        {item.workname}
                      </div>
                      <div className='tag-work-author-info' title={item.authorname}>
                        {item.dynasty ? item.dynasty + '/' : ''}
                        {item.authorname}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            '暂无数据'
          )}
        </div>
      </div>
      <div className='detail-menu'>
        <div className='detail-menu-list'>
          {menuList.map((item, index) => {
            return (
              <div
                className={'detail-menu-item ' + (selectedMenuIndex === index ? '' : 'unselected')}
                key={item}
                onClick={(_e) => handleSelectedIndex(index)}
              >
                {item}
              </div>
            )
          })}
        </div>
        <div className='detail-menu-bar'>
          <div className='col-bar'></div>
          {menuList.map((item, index) => {
            return (
              <div
                className='row-bar'
                style={{
                  opacity: index === selectedMenuIndex ? 1 : 0.43
                }}
                key={item}
              ></div>
            )
          })}
        </div>
      </div>
      {/* 瓦片地图 */}
      <div
        className={'painting-container ' + (isDisplayDownBtn ? 'painting-container-list-open' : '')}
      >
        <MiniMap
          tilesPath={showWorkList[selectedWorkIndex]?.tilesPath ?? ''}
          width={showWorkList[selectedWorkIndex]?.width ?? 0}
          height={showWorkList[selectedWorkIndex]?.height ?? 0}
          levelMax={showWorkList[selectedWorkIndex]?.levelMax ?? 1}
          levelMin={showWorkList[selectedWorkIndex]?.levelMin ?? 1}
          displayType={0}
        />
      </div>
      {/* 画作列表 */}
      <div
        className='painting-select-content'
        id='painting-select-content'
        style={{ display: showWorkList.length > 1 ? 'flex' : 'none' }}
      >
        <SelectWorkList
          showWorkList={showWorkList}
          selectedWorkIndex={selectedWorkIndex}
          setSelectedWorkIndex={setSelectedWorkIndex}
          listSize={listSize}
          type={0}
        />
      </div>
      <div
        className='down-btn'
        onClick={handlePackUp}
        style={{ display: isDisplayDownBtn ? 'block' : 'none' }}
      >
        <img src={downBtn} alt='收起' />
      </div>
      <div
        className='pull-up-btn'
        style={{ display: isDisplayPullUp ? 'block' : 'none' }}
        onClick={handlePullUp}
      >
        <img src={upBtn} alt='上拉' />
      </div>
    </div>
  )
}
